import React, { createContext, useState, useContext } from 'react';

// Define our translations
export const translations = {
  en: {
    // Navbar
    about: "About",
    coaching: "Coaching",
    speaking: "Speaking",
    testimonials: "Testimonials",
    contact: "Contact",
    
    // Hero Section
    heroTitle: "Transform Your Potential",
    heroSubtitle: "Strategic coaching and impactful speaking to unlock breakthrough results.",
    exploreCoaching: "Explore Coaching",
    bookSpeaker: "Book as Speaker",
    
    // About Section
    aboutTitle: "About Tudor Emanuel",
    
    // Coaching Section
    coachingTitle: "Elite 1-on-1 Coaching",
    coachingSubtitle: "Personalized guidance to unlock your potential and achieve extraordinary results.",
    transformTitle: "Transform Your Life & Business",
    coachingDescription: "My premium coaching program is designed for ambitious professionals and entrepreneurs who are ready to break through limitations and reach new heights of success.",
    coachingProcess: "My Coaching Process:",
    assessment: "Deep-Dive Assessment",
    assessmentDesc: "Identify your strengths, challenges, and most impactful opportunities",
    planning: "Strategic Planning",
    planningDesc: "Create a customized roadmap for achieving breakthrough results",
    implementation: "Implementation Support",
    implementationDesc: "Weekly guidance, accountability, and course correction",
    methods: "Breakthrough Methods",
    methodsDesc: "Access to proven tools and strategies for lasting transformation",
    applyCoaching: "Apply for Coaching",
    readyTransform: "Ready to Transform Your Life?",
    scheduleSession: "Schedule a free 30-minute strategy session to see if we're a good fit for working together.",
    bookCall: "Book Your Strategy Call",
    
    // Speaking Section
    speakingTitle: "Conference Speaking",
    speakingSubtitle: "Elevate your event with impactful presentations that inspire meaningful change and action.",
    transformEvent: "Transform Your Next Event",
    speakingDescription: "With over a decade of experience speaking at conferences worldwide, I deliver engaging, high-impact presentations tailored to your audience and objectives.",
    topicsTitle: "Popular Speaking Topics:",
    leadership: "Leadership Transformation:",
    leadershipDesc: "Building resilient, innovative teams in changing environments",
    growth: "Strategic Growth:",
    growthDesc: "Frameworks for sustainable business expansion and market leadership",
    excellence: "Personal Excellence:",
    excellenceDesc: "Peak performance strategies for breakthrough results",
    bookEvent: "Book for Your Event",
    viewPortfolio: "View Speaking Portfolio",
    
    // Testimonials
    testimonialsTitle: "What Clients Say",
    testimonialsSubtitle: "Real results from clients who have experienced my coaching and speaking programs.",
    
    // Footer
    footerRights: "All rights reserved."
  },
  es: {
    // Navbar
    about: "Acerca de",
    coaching: "Coaching",
    speaking: "Conferencias",
    testimonials: "Testimonios",
    contact: "Contacto",
    
    // Hero Section
    heroTitle: "Transforma Tu Potencial",
    heroSubtitle: "Coaching estratégico y conferencias impactantes para lograr resultados extraordinarios.",
    exploreCoaching: "Explorar Coaching",
    bookSpeaker: "Contratar como Conferenciante",
    
    // About Section
    aboutTitle: "Acerca de Tudor Emanuel",
    
    // Coaching Section
    coachingTitle: "Coaching Personalizado de Élite",
    coachingSubtitle: "Orientación personalizada para desbloquear tu potencial y lograr resultados extraordinarios.",
    transformTitle: "Transforma Tu Vida y Negocio",
    coachingDescription: "Mi programa de coaching premium está diseñado para profesionales y emprendedores ambiciosos que están listos para superar limitaciones y alcanzar nuevas alturas de éxito.",
    coachingProcess: "Mi Proceso de Coaching:",
    assessment: "Evaluación Profunda",
    assessmentDesc: "Identificar tus fortalezas, desafíos y oportunidades de mayor impacto",
    planning: "Planificación Estratégica",
    planningDesc: "Crear un plan personalizado para lograr resultados extraordinarios",
    implementation: "Apoyo en la Implementación",
    implementationDesc: "Orientación semanal, responsabilidad y ajustes de curso",
    methods: "Métodos Innovadores",
    methodsDesc: "Acceso a herramientas y estrategias probadas para una transformación duradera",
    applyCoaching: "Solicitar Coaching",
    readyTransform: "¿Listo para Transformar Tu Vida?",
    scheduleSession: "Programa una sesión estratégica gratuita de 30 minutos para ver si somos compatibles para trabajar juntos.",
    bookCall: "Reserva Tu Llamada Estratégica",
    
    // Speaking Section
    speakingTitle: "Conferencias",
    speakingSubtitle: "Eleva tu evento con presentaciones impactantes que inspiran cambios significativos y acción.",
    transformEvent: "Transforma Tu Próximo Evento",
    speakingDescription: "Con más de una década de experiencia en conferencias en todo el mundo, ofrezco presentaciones atractivas y de alto impacto adaptadas a tu audiencia y objetivos.",
    topicsTitle: "Temas Populares:",
    leadership: "Transformación del Liderazgo:",
    leadershipDesc: "Construir equipos resilientes e innovadores en entornos cambiantes",
    growth: "Crecimiento Estratégico:",
    growthDesc: "Marcos para la expansión sostenible del negocio y el liderazgo en el mercado",
    excellence: "Excelencia Personal:",
    excellenceDesc: "Estrategias de alto rendimiento para resultados extraordinarios",
    bookEvent: "Reservar para Tu Evento",
    viewPortfolio: "Ver Portafolio de Conferencias",
    
    // Testimonials
    testimonialsTitle: "Lo Que Dicen Los Clientes",
    testimonialsSubtitle: "Resultados reales de clientes que han experimentado mis programas de coaching y conferencias.",
    
    // Footer
    footerRights: "Todos los derechos reservados."
  }
};

// Create the language context
const LanguageContext = createContext();

// Create a provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en'); // Default language is English
  
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };
  
  const t = (key) => {
    return translations[language][key] || key;
  };
  
  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Create a custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
